import CommonIriDisplay from '../../application-core/CommonIriDisplay';
import QualityCheckLoader from '../../application-core/QualityCheckLoader';
import { Typography } from '@material-ui/core';
import Chip from '@material-ui/core/Chip';
import { withStyles } from '@material-ui/core/styles';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import ArrowLeftIcon from '@material-ui/icons/ArrowLeft';
import CompareArrowsIcon from '@material-ui/icons/CompareArrows';
import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router-dom';
import LanguageAvatar from '../../core/LanguageAvatar';
import { globalStyles as styles } from '../../core/style/gstheme';
import QualityCheck from '../../application-core/QualityCheck';
import { checkResourceEntitlements, getProjectFromGraph } from '../../core/utils/utils';

const views = [
    "labelintegrity",
    "multiplepreflabels",
    "preflabelduplication",
    "conceptcycleclashes",
    "relationshipintegrity",
    "matchintegrity",
    "disjointclasses",
    "disjointclasseslabels",
    "disjointproperties",
    "disjointpropertieslabels",
    "monohierarchy",
    "multiplepropvalues",
    "multiplepropvalueslabels",
    "associatedpropertyrange",
    "associatedpropertyrangelabels",
    "asymmetric",
    "asymmetriclabels"
];

const viewsRequiringOntologies = [
    "multiplepropvalues",
    "multiplepropvalueslabels",
    "associatedpropertyrange",
    "associatedpropertyrangelabels",
    "asymmetric",
    "asymmetriclabels",
    "disjointclasses",
    "disjointclasseslabels",
    "disjointproperties",
    "disjointpropertieslabels",
];

const viewsRequiringXL = [
    "multiplepropvalueslabels",
    "associatedpropertyrangelabels",
    "disjointclasseslabels",
    "asymmetriclabels",
    "disjointpropertieslabels",
];

const labelReport = [
    {
        prop: "prefAltLabelViolation",
        message: "The following values have been used for both preferred and alternative labels on the same concept."
    },
    {
        prop: "prefHiddenLabelViolation",
        message: "The following values have been used for both preferred and hidden labels on the same concept."
    },
    {
        prop: "altHiddenLabelViolation",
        message: "The following values have been used for both alternative and hidden labels on the same concept."
    },
    {
        prop: "altAltLabelViolation",
        message: "The following values have been used multiple times for alternative labels on the same concept."
    },
    {
        prop: "hiddenHiddenLabelViolation",
        message: "The following values have been used multiple times for hidden labels on the same concept."
    }
];

class SchemeQuality extends React.Component {

    constructor(props) {
        super(props);
        // We filter out views that require ontologies if no associated projects
        // We filter out XL views if no XL
        const filteredViews = views
            .filter(view => !viewsRequiringOntologies.includes(view)
                || (props.ontologyProjectAssociation && props.ontologyProjectAssociation.length > 0))
            .filter(view => !viewsRequiringXL.includes(view) || props.skosXL)
            .filter(view => view !== "monohierarchy" || this.props.data.editingMode === "Mono");
        this.state = {
            filteredViews
        };
    }

    displayResults = (issues, isLoading, onRefresh) => {
        const { classes, ontologyProjectAssociation, skosXL, graph, onToggleView, defaultLang, onDisable, data, entitlement } = this.props;
        const disabledFeatures = data.disabledFeatures;
        const isReadOnly = checkResourceEntitlements(data, entitlement);
        return <div>
            <QualityCheck
                dataTest="label-integrity"
                title="Label Integrity Within A Concept"
                issues={issues.labelintegrity}
                isLoading={isLoading}
                isReadOnly={isReadOnly}
                defaultLang={defaultLang}
                sortLabel="prefLabel"
                graph={graph}
                meaning={<div>
                    <Typography variant="body2" color="textPrimary">The SKOS specification states that all preferred, alternative and hidden labels must be unique on a single concept.</Typography>
                    <Typography variant="body2" color="textPrimary">You can resolve these issues by removing or changing the labels.</Typography>
                </div>}
                loadProps={["id"]}
                errors={issues["labelintegrity-error"]}
                disabled={disabledFeatures && disabledFeatures.includes("quality-labelintegrity")}
                onDisable={(disable) => onDisable("labelintegrity", disable)}
                onRefresh={() => onRefresh("labelintegrity")}
                fromCache={issues["labelintegrity-time"]}
                issueDisplay={
                    (li) => {
                        return labelReport.map((lr, lrIndex) =>
                            li[lr.prop] &&
                            <div data-test={"lr-" + lrIndex} key={li.id + "-" + lr.prop} style={{ marginBottom: 10 }}>
                                <Typography data-test="qm" variant="body2" paragraph color="textPrimary">{lr.message}</Typography>
                                <CommonIriDisplay
                                    key={li.id}
                                    id={li.id}
                                    compact={true}
                                    projectIRI={getProjectFromGraph(graph)}
                                    handleNotAvailable={true}
                                    loadComponents={true}
                                    graph={graph}
                                    wrap={false}
                                    onLink={onToggleView}
                                    viewLang={defaultLang}
                                    defaultLang={defaultLang} />
                                <ArrowRightIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                {Object.keys(li[lr.prop]).sort().map(v => {
                                    const vLabels = Array.isArray(li[lr.prop][v]) ? li[lr.prop][v] : [li[lr.prop][v]];
                                    return vLabels.sort().map((vLabel, vIndex) => <Chip
                                        color="primary"
                                        className={classes.labelChip}
                                        data-test={"qm-chip-" + vIndex}
                                        key={"chip-" + vIndex}
                                        avatar={<LanguageAvatar lang={v} />}
                                        label={vLabel} />
                                    );
                                })}
                            </div>
                        );
                    }
                }
            />
            <QualityCheck
                dataTest="multiple-preferred-labels"
                title="Multiple Preferred Labels For The Same Language"
                issues={issues.multiplepreflabels}
                isLoading={isLoading}
                graph={graph}
                isReadOnly={isReadOnly}
                sortLabel="prefLabel"
                defaultLang={defaultLang}
                loadProps={["id"]}
                meaning={<div>
                    <Typography variant="body2" color="textPrimary">The SKOS specification states that only one preferred label per language is allowed.</Typography>
                    <Typography variant="body2" color="textPrimary">You can resolve these issues by removing the additional labels.</Typography>
                </div>}
                disabled={disabledFeatures && disabledFeatures.includes("quality-multiplepreflabels")}
                onDisable={(disable) => onDisable("multiplepreflabels", disable)}
                onRefresh={() => onRefresh("multiplepreflabels")}
                errors={issues["multiplepreflabels-error"]}
                fromCache={issues["multiplepreflabels-time"]}
                issueDisplay={
                    (li) => <div key={li.id} style={{ marginBottom: 10 }}>
                        <CommonIriDisplay
                            key={li.id}
                            id={li.id}
                            compact={true}
                            projectIRI={getProjectFromGraph(graph)}
                            handleNotAvailable={true}
                            loadComponents={true}
                            graph={graph}
                            wrap={false}
                            onLink={onToggleView}
                            viewLang={defaultLang}
                            defaultLang={defaultLang} />
                        <ArrowRightIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                        {Object.keys(li.multiplePrefLabelViolation).sort().map(v => {
                            return li.multiplePrefLabelViolation[v].sort().map((text, c) =>
                                <Chip color="primary"
                                    data-test="label-text"
                                    className={classes.labelChip}
                                    key={li.id + "-" + c}
                                    avatar={<LanguageAvatar lang={v} />}
                                    style={{ paddingLeft: '4px' }}
                                    label={text} />
                            );
                        }
                        )}
                    </div>
                }
            />
            <QualityCheck
                dataTest="same-preferred-label"
                title="Same Preferred Label On Different Concepts"
                issues={issues.preflabelduplication}
                isLoading={isLoading}
                graph={graph}
                isReadOnly={isReadOnly}
                sortLabel="prefLabel"
                defaultLang={defaultLang}
                loadProps={["id", "samePrefLabelViolation"]}
                disabled={disabledFeatures && disabledFeatures.includes("quality-preflabelduplication")}
                onDisable={(disable) => onDisable("preflabelduplication", disable)}
                onRefresh={() => onRefresh("preflabelduplication")}
                errors={issues["preflabelduplication-error"]}
                fromCache={issues["preflabelduplication-time"]}
                meaning={<div>
                    <Typography variant="body2" color="textPrimary">The SKOS specification states that preferred labels must be unique across a concept scheme.</Typography>
                    <Typography variant="body2" color="textPrimary">There are multiple concepts in the taxonomy with the same preferred label.</Typography>
                    <Typography variant="body2" color="textPrimary">You can resolve these issues by changing some of the labels.</Typography>
                </div>}
                issueDisplay={
                    (li) => <div key={li.id} style={{ marginBottom: 10 }}>
                        <CommonIriDisplay
                            key={li.id}
                            id={li.id}
                            compact={true}
                            projectIRI={getProjectFromGraph(graph)}
                            handleNotAvailable={true}
                            loadComponents={true}
                            graph={graph}
                            wrap={false}
                            onLink={onToggleView}
                            viewLang={defaultLang}
                            defaultLang={defaultLang} />
                        <ArrowRightIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                        <Chip
                            color="primary"
                            data-test="common-label"
                            className={classes.labelChip}
                            avatar={<LanguageAvatar lang={Object.keys(li.prefLabel)[0]} />}
                            style={{ paddingLeft: '4px' }}
                            label={li.prefLabel[Object.keys(li.prefLabel)[0]]} />
                        <ArrowLeftIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                        {li.samePrefLabelViolation.sort().map((v, vIndex) => {
                            return <CommonIriDisplay
                                id={v}
                                key={vIndex}
                                compact={true}
                                projectIRI={getProjectFromGraph(graph)}
                                handleNotAvailable={true}
                                loadComponents={true}
                                graph={graph}
                                wrap={false}
                                onLink={onToggleView}
                                viewLang={defaultLang}
                                defaultLang={defaultLang} />;
                        })}
                    </div>
                }
            />
            <QualityCheck
                dataTest="concept-cycle"
                title="Concept Cycle Violations"
                issues={issues.conceptcycleclashes}
                isLoading={isLoading}
                graph={graph}
                isReadOnly={isReadOnly}
                sortLabel="prefLabel"
                defaultLang={defaultLang}
                loadProps={["id", "cyclicWith"]}
                disabled={disabledFeatures && disabledFeatures.includes("quality-conceptcycleclashes")}
                onDisable={(disable) => onDisable("conceptcycleclashes", disable)}
                onRefresh={() => onRefresh("conceptcycleclashes")}
                meaning={<div>
                    <Typography variant="body2" color="textPrimary">Concepts in the hierarchy should not be both broader and narrower to another concept as this causes cyclical issues.</Typography>
                    <Typography variant="body2" color="textPrimary">The following pairs of concepts have broader and narrower relationships with each other at some point in the hierarchy.</Typography>
                    <Typography variant="body2" color="textPrimary">You can resolve these issues by breaking some of the links in the hierarchy.</Typography>
                </div>}
                errors={issues["conceptcycleclashes-error"]}
                fromCache={issues["conceptcycleclashes-time"]}
                issueDisplay={
                    (cc) => cc.cyclicWith && cc.cyclicWith.map(v => {
                        return <div key={v} style={{ marginBottom: 10 }}>
                            <CommonIriDisplay
                                key={"left-" + cc.id}
                                id={cc.id}
                                compact={true}
                                projectIRI={getProjectFromGraph(graph)}
                                handleNotAvailable={true}
                                loadComponents={true}
                                graph={graph}
                                wrap={false}
                                onLink={onToggleView}
                                viewLang={defaultLang}
                                defaultLang={defaultLang} />
                            <CompareArrowsIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                            <CommonIriDisplay
                                key={"right-" + v}
                                id={v}
                                compact={true}
                                projectIRI={getProjectFromGraph(graph)}
                                handleNotAvailable={true}
                                loadComponents={true}
                                graph={graph}
                                wrap={false}
                                onLink={onToggleView}
                                viewLang={defaultLang}
                                defaultLang={defaultLang} />
                        </div>;
                    })
                }
            />
            <QualityCheck
                dataTest="associative-relationship"
                title="Associative And Hierarchical Relationship Integrity"
                issues={issues.relationshipintegrity}
                isLoading={isLoading}
                graph={graph}
                isReadOnly={isReadOnly}
                sortLabel="prefLabel"
                defaultLang={defaultLang}
                loadProps={["id", "narrowerRelatedRelationClash", "broaderRelatedRelationClash"]}
                disabled={disabledFeatures && disabledFeatures.includes("quality-relationshipintegrity")}
                onDisable={(disable) => onDisable("relationshipintegrity", disable)}
                onRefresh={() => onRefresh("relationshipintegrity")}
                meaning={<div>
                    <Typography variant="body2" color="textPrimary">The SKOS specification states that there should not be multiple associative relationships on the same concept to another concept.</Typography>
                    <Typography variant="body2" color="textPrimary">The following pairs of concepts have multiple relationships as explained below.</Typography>
                    <Typography variant="body2" color="textPrimary">You can resolve these issues by removing some of the relationships.</Typography>
                </div>}
                errors={issues["relationshipintegrity-error"]}
                fromCache={issues["relationshipintegrity-time"]}
                message={<span>The following pairs of concepts have associative and hierarchical relationship conflicts.</span>}
                issueDisplay={
                    (ri) => {
                        let property = ri.narrowerRelatedRelationClash ? "narrowerRelatedRelationClash" : "broaderRelatedRelationClash";
                        return <div>
                            {ri[property] &&
                                <Typography paragraph variant="body2" color="textPrimary">Via broader/narrower and related relationships:</Typography>
                            }
                            {ri[property] && ri[[property]].map((v, vIndex) => {
                                return <div
                                    data-test={"associative-error-" + vIndex}
                                    key={v}
                                    style={{ marginBottom: 10 }}>
                                    <CommonIriDisplay
                                        key={"left-" + ri.id}
                                        id={ri.id}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={defaultLang}
                                        defaultLang={defaultLang} />
                                    <CompareArrowsIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                    <CommonIriDisplay
                                        key={"right-" + v}
                                        id={v}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={defaultLang}
                                        defaultLang={defaultLang} />
                                </div>;
                            })}
                        </div>;
                    }}
            />
            <QualityCheck
                dataTest="mapping-relationship"
                title="Mapping Relationship Integrity"
                issues={issues.matchintegrity}
                isLoading={isLoading}
                graph={graph}
                isReadOnly={isReadOnly}
                sortLabel="prefLabel"
                defaultLang={defaultLang}
                loadProps={["id", "mappingRelationClash"]}
                disabled={disabledFeatures && disabledFeatures.includes("quality-matchintegrity")}
                onDisable={(disable) => onDisable("matchintegrity", disable)}
                onRefresh={() => onRefresh("matchintegrity")}
                meaning={<div>
                    <Typography variant="body2" color="textPrimary">The SKOS specification states that there should not be multiple  mapping relationships on the same concept to another concept.</Typography>
                    <Typography variant="body2" color="textPrimary">The following pairs of concepts have multiple mappings.</Typography>
                    <Typography variant="body2" color="textPrimary">You can resolve these issues by checking and removing some of the mappings for Exact Match, Related Match, Close Match, Narrow Match and Broad Match.</Typography>
                </div>}
                errors={issues["matchintegrity-error"]}
                fromCache={issues["matchintegrity-time"]}
                issueDisplay={
                    (ri) => {
                        return <div>
                            {ri["mappingRelationClash"] && ri[["mappingRelationClash"]].map((v, vIndex) => {
                                return <div
                                    data-test={"mapping-error-" + vIndex}
                                    key={v}
                                    style={{ marginBottom: 10 }}>
                                    <CommonIriDisplay
                                        key={"left-" + ri.id}
                                        id={ri.id}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={defaultLang}
                                        defaultLang={defaultLang} />
                                    <CompareArrowsIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                    <CommonIriDisplay
                                        key={"right-" + v}
                                        id={v}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={defaultLang}
                                        defaultLang={defaultLang} />
                                </div>;
                            })}
                        </div>;
                    }}
            />
            {ontologyProjectAssociation &&
                <QualityCheck
                    dataTest="disjoint-classes"
                    title="Disjoint Classes On The Same Concept"
                    issues={issues.disjointclasses}
                    isLoading={isLoading}
                    graph={graph}
                    isReadOnly={isReadOnly}
                    sortLabel="prefLabel"
                    defaultLang={defaultLang}
                    loadProps={["id", "disjointClassViolation"]}
                    disabled={disabledFeatures && disabledFeatures.includes("quality-disjointclasses")}
                    onDisable={(disable) => onDisable("disjointclasses", disable)}
                    onRefresh={() => onRefresh("disjointclasses")}
                    errors={issues["disjointclasses-error"]}
                    fromCache={issues["disjointclasses-time"]}
                    meaning={<div>
                        <Typography variant="body2" color="textPrimary">Disjoint classes means that no concept should have both classes, as it is not possible to be both classes at the same time.</Typography>
                        <Typography variant="body2" color="textPrimary">You can possibly resolve these issues by removing one of the classes on each concept, or checking whether the classes should actually be disjoint in the ontology.</Typography>
                    </div>}
                    issueDisplay={
                        (dw) => {
                            return dw.disjointClassViolation &&
                                <div style={{ marginBottom: 10 }}>
                                    <CommonIriDisplay
                                        key={dw.id}
                                        id={dw.id}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={defaultLang}
                                        defaultLang={defaultLang} />
                                    <ArrowLeftIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                    {dw.disjointClassViolation.sort().map(v =>
                                        <Chip key={v} color="primary" className={classes.inactiveChip} label={v} />
                                    )}
                                </div>;
                        }}
                />
            }
            {ontologyProjectAssociation && skosXL &&
                <QualityCheck
                    dataTest="disjoint-classes-labels"
                    title="Disjoint Classes On The Same Label"
                    issues={issues.disjointclasseslabels}
                    isLoading={isLoading}
                    graph={graph}
                    isReadOnly={isReadOnly}
                    sortLabel="prefLabel"
                    loadProps={["id", "disjointClassViolation"]}
                    disabled={disabledFeatures && disabledFeatures.includes("quality-disjointclasseslabels")}
                    onDisable={(disable) => onDisable("disjointclasseslabels", disable)}
                    onRefresh={() => onRefresh("disjointclasseslabels")}
                    errors={issues["disjointclasseslabels-error"]}
                    fromCache={issues["disjointclasseslabels-time"]}
                    meaning={<div>
                        <Typography variant="body2" color="textPrimary">Disjoint classes means that no label should have both classes, as it is not possible to be both classes at the same time.</Typography>
                        <Typography variant="body2" color="textPrimary">You can possibly resolve these issues by removing one of the classes on each label, or checking whether the classes should actually be disjoint in the ontology.</Typography>
                    </div>}
                    message={<span>The following labels have disjoint classes.</span>}
                    issueDisplay={
                        (dw) => {
                            return dw.disjointClassViolation &&
                                <div style={{ marginBottom: 10 }}>
                                    <CommonIriDisplay
                                        key={dw.id}
                                        id={dw.id}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={defaultLang}
                                        defaultLang={defaultLang} />
                                    <ArrowLeftIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                    {dw.disjointClassViolation.sort().map(v =>
                                        <Chip key={v} color="primary" className={classes.inactiveChip} label={v} />
                                    )}
                                </div>;
                        }}
                />
            }
            {ontologyProjectAssociation &&
                <QualityCheck
                    dataTest="disjoint-properties"
                    title="Clashing Values on Disjoint Properties on Concepts"
                    issues={issues.disjointproperties}
                    isLoading={isLoading}
                    graph={graph}
                    isReadOnly={isReadOnly}
                    sortLabel="prefLabel"
                    defaultLang={defaultLang}
                    loadProps={["id", "disjointPropertyViolation"]}
                    disabled={disabledFeatures && disabledFeatures.includes("quality-disjointproperties")}
                    onDisable={(disable) => onDisable("disjointproperties", disable)}
                    onRefresh={() => onRefresh("disjointproperties")}
                    errors={issues["disjointproperties-error"]}
                    fromCache={issues["disjointproperties-time"]}
                    meaning={<div>
                        <Typography variant="body2" color="textPrimary">Disjoint properties means that those properties should not have the same value at the same time.</Typography>
                        <Typography variant="body2" color="textPrimary">You can possibly resolve these issues by removing one of the values, or checking whether the properties should actually be disjoint in the ontology.</Typography>
                    </div>}
                    issueDisplay={
                        (dp, issues) => {
                            return dp.disjointPropertyViolation &&
                                <div style={{ marginBottom: 10 }}>
                                    <CommonIriDisplay
                                        key={dp.id}
                                        id={dp.id}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={defaultLang}
                                        defaultLang={defaultLang} />
                                    <ArrowLeftIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                    {dp.disjointPropertyViolation.sort().map(v => {
                                        const propertyItem = issues.find(item => item.id === v);
                                        // Just allow for edge case of same IRI in two projects!
                                        const propertyGraph = Array.isArray(propertyItem.inNamedGraph) ?
                                            propertyItem.inNamedGraph[0]
                                            :
                                            propertyItem.inNamedGraph;
                                        return <CommonIriDisplay
                                            id={v}
                                            key={v}
                                            compact={true}
                                            projectIRI={getProjectFromGraph(propertyGraph)}
                                            handleNotAvailable={true}
                                            loadComponents={true}
                                            graph={propertyGraph}
                                            wrap={false}
                                            onLink={onToggleView}
                                            viewLang={defaultLang}
                                            defaultLang={defaultLang} />;
                                    })}
                                </div>;
                        }}
                />
            }
            {ontologyProjectAssociation && skosXL &&
                <QualityCheck
                    dataTest="disjoint-properties-labels"
                    title="Clashing Values on Disjoint Properties on XL Labels"
                    issues={issues.disjointpropertieslabels}
                    isLoading={isLoading}
                    graph={graph}
                    isReadOnly={isReadOnly}
                    sortLabel="literalForm"
                    defaultLang={defaultLang}
                    loadProps={["id", "disjointPropertyViolation"]}
                    disabled={disabledFeatures && disabledFeatures.includes("quality-disjointpropertieslabels")}
                    onDisable={(disable) => onDisable("disjointpropertieslabels", disable)}
                    onRefresh={() => onRefresh("disjointpropertieslabels")}
                    errors={issues["disjointpropertieslabels-error"]}
                    fromCache={issues["disjointpropertieslabels-time"]}
                    meaning={<div>
                        <Typography variant="body2" color="textPrimary">Disjoint properties means that those properties should not have the same value at the same time.</Typography>
                        <Typography variant="body2" color="textPrimary">You can possibly resolve these issues by removing one of the values, or checking whether the properties should actually be disjoint in the ontology.</Typography>
                    </div>}
                    issueDisplay={
                        (dp, issues) => {
                            return dp.disjointPropertyViolation &&
                                <div style={{ marginBottom: 10 }}>
                                    <CommonIriDisplay
                                        key={dp.id}
                                        id={dp.id}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={defaultLang}
                                        defaultLang={defaultLang} />
                                    <ArrowLeftIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                    {dp.disjointPropertyViolation.sort().map(v => {
                                        const propertyItem = issues.find(item => item.id === v);
                                        // Just allow for edge case of same IRI in two projects!
                                        const propertyGraph = Array.isArray(propertyItem.inNamedGraph) ?
                                            propertyItem.inNamedGraph[0]
                                            :
                                            propertyItem.inNamedGraph;
                                        return <CommonIriDisplay
                                            id={v}
                                            key={v}
                                            compact={true}
                                            projectIRI={getProjectFromGraph(propertyGraph)}
                                            handleNotAvailable={true}
                                            loadComponents={true}
                                            graph={propertyGraph}
                                            wrap={false}
                                            onLink={onToggleView}
                                            viewLang={defaultLang}
                                            defaultLang={defaultLang} />;
                                    })}
                                </div>;
                        }}
                />
            }
            {this.props.data.editingMode === "Mono" && <QualityCheck
                dataTest="monohierarchy"
                title="Monohierarchy Violations"
                issues={issues.monohierarchy}
                isLoading={isLoading}
                graph={graph}
                isReadOnly={isReadOnly}
                sortLabel="prefLabel"
                defaultLang={defaultLang}
                loadProps={["id", "monohierarchyViolation"]}
                disabled={disabledFeatures && disabledFeatures.includes("quality-monohierarchy")}
                onDisable={(disable) => onDisable("monohierarchy", disable)}
                onRefresh={() => onRefresh("monohierarchy")}
                errors={issues["monohierarchy-error"]}
                fromCache={issues["monohierarchy-time"]}
                meaning={<div>
                    <Typography variant="body2" color="textPrimary">In a monohierarchical taxonomy concepts should only appear once in the hierarchy.</Typography>
                    <Typography variant="body2" color="textPrimary">Certain concepts in this taxonomy appear multiple times.</Typography>
                    <Typography variant="body2" color="textPrimary">You can resolve these issues by removing the concept from one or more places.</Typography>
                </div>}
                issueDisplay={
                    (mh) => {
                        return mh.monohierarchyViolation &&
                            <div style={{ marginBottom: 10 }}>
                                <CommonIriDisplay
                                    key={mh.id}
                                    id={mh.id}
                                    compact={true}
                                    projectIRI={getProjectFromGraph(graph)}
                                    handleNotAvailable={true}
                                    loadComponents={true}
                                    graph={graph}
                                    wrap={false}
                                    onLink={onToggleView}
                                    viewLang={defaultLang}
                                    defaultLang={defaultLang} />
                                <ArrowRightIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                {mh.monohierarchyViolation.sort().map(v => {
                                    return <CommonIriDisplay
                                        id={v}
                                        key={v}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={defaultLang}
                                        defaultLang={defaultLang} />;
                                })}
                            </div>;
                    }}
            />
            }
            {ontologyProjectAssociation &&
                <QualityCheck
                    dataTest="multiple-property-values"
                    title="Multiple Property Values For A Functional Property On A Concept"
                    issues={issues.multiplepropvalues}
                    isLoading={isLoading}
                    graph={graph}
                    isReadOnly={isReadOnly}
                    sortLabel="prefLabel"
                    defaultLang={defaultLang}
                    loadProps={["id"]}
                    onDisable={(disable) => onDisable("multiplepropvalues", disable)}
                    onRefresh={() => onRefresh("multiplepropvalues")}
                    disabled={disabledFeatures && disabledFeatures.includes("quality-multiplepropvalues")}
                    errors={issues["multiplepropvalues-error"]}
                    fromCache={issues["multiplepropvalues-time"]}
                    meaning={<div>
                        <Typography variant="body2" color="textPrimary">Functional properties should only have one value.</Typography>
                        <Typography variant="body2" color="textPrimary">The following concepts have multiple values for the same property.</Typography>
                        <Typography variant="body2" color="textPrimary">You can resolve these issues by removing the additional values.</Typography>
                    </div>}
                    issueDisplay={
                        (li, issues) => {
                            return li.multiplePropValueViolation &&
                                <div key={li.id} style={{ marginBottom: 10 }}>
                                    <CommonIriDisplay
                                        key={li.id}
                                        id={li.id}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={defaultLang}
                                        defaultLang={defaultLang} />
                                    <ArrowRightIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                    {li.multiplePropValueViolation.sort().map((v, vIndex) => {
                                        const labelItem = issues.find(item => item.id === v);
                                        const labelLang = Object.keys(labelItem.label)[0];
                                        const label = labelItem ?
                                            labelItem.label[labelLang]
                                            :
                                            v.latestIRI || v;
                                        return labelLang ?
                                            <Chip
                                                data-test={"value-" + vIndex}
                                                color="primary"
                                                className={classes.labelChip}
                                                key={"chip-" + v}
                                                avatar={<LanguageAvatar lang={labelLang} />}
                                                label={label} />
                                            :
                                            <Chip key={v} color="primary" className={classes.inactiveChip} label={label} />;
                                    }
                                    )}
                                </div>;
                        }
                    }
                />
            }
            {ontologyProjectAssociation && skosXL &&
                <QualityCheck
                    dataTest="multiple-property-values-labels"
                    title="Multiple Property Values For A Functional Property On A Label"
                    issues={issues.multiplepropvalueslabels}
                    isLoading={isLoading}
                    graph={graph}
                    isReadOnly={isReadOnly}
                    sortLabel="prefLabel"
                    defaultLang={defaultLang}
                    loadProps={["id"]}
                    onDisable={(disable) => onDisable("multiplepropvalueslabels", disable)}
                    onRefresh={() => onRefresh("multiplepropvalueslabels")}
                    disabled={disabledFeatures && disabledFeatures.includes("quality-multiplepropvalueslabels")}
                    errors={issues["multiplepropvalueslabels-error"]}
                    fromCache={issues["multiplepropvalueslabels-time"]}
                    meaning={<div>
                        <Typography variant="body2" color="textPrimary">Functional properties should only have one value.</Typography>
                        <Typography variant="body2" color="textPrimary">The following labels have multiple values for the same property.</Typography>
                        <Typography variant="body2" color="textPrimary">You can resolve these issues by removing the additional values.</Typography>
                    </div>}
                    issueDisplay={
                        (li, issues) => {
                            return li.multiplePropValueViolation &&
                                <div key={li.id} style={{ marginBottom: 10 }}>
                                    <CommonIriDisplay
                                        key={li.id}
                                        id={li.id}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={defaultLang}
                                        defaultLang={defaultLang} />
                                    <ArrowRightIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                    {li.multiplePropValueViolation.sort().map((v, vIndex) => {
                                        const labelItem = issues.find(item => item.id === v);
                                        const labelLang = Object.keys(labelItem.label)[0];
                                        const label = labelItem ?
                                            labelItem.label[labelLang]
                                            :
                                            v.latestIRI || v;
                                        return labelLang ?
                                            <Chip
                                                data-test={"value-" + vIndex}
                                                color="primary"
                                                className={classes.labelChip}
                                                key={"chip-" + v}
                                                avatar={<LanguageAvatar lang={labelLang} />}
                                                label={label} />
                                            :
                                            <Chip key={v} color="primary" className={classes.inactiveChip} label={label} />;
                                    }
                                    )}
                                </div>;
                        }
                    }
                />
            }
            {ontologyProjectAssociation &&
                <QualityCheck
                    dataTest="associated-property"
                    title="Associated Property Range Integrity On A Concept"
                    issues={issues.associatedpropertyrange}
                    isLoading={isLoading}
                    graph={graph}
                    isReadOnly={isReadOnly}
                    sortLabel="prefLabel"
                    defaultLang={defaultLang}
                    loadProps={["id", "inconsistentWithRange"]}
                    disabled={disabledFeatures && disabledFeatures.includes("quality-associatedpropertyrange")}
                    onDisable={(disable) => onDisable("associatedpropertyrange", disable)}
                    onRefresh={() => onRefresh("associatedpropertyrange")}
                    errors={issues["associatedpropertyrange-error"]}
                    fromCache={issues["associatedpropertyrange-time"]}
                    meaning={<div>
                        <Typography variant="body2" color="textPrimary">If a property has a range it is expected that the target resource will have the range as a class.</Typography>
                        <Typography variant="body2" color="textPrimary">The following concepts use an associated property whose target does not have classes that are stated on the range of the associated property.</Typography>
                        <Typography variant="body2" color="textPrimary">You can probably resolve these issues by adding the class to the target, or alternatively, check the range on the property.</Typography>
                    </div>}
                    issueDisplay={
                        (iwr) => {
                            return iwr.inconsistentWithRange && iwr.inconsistentWithRange.map(v =>
                                <div key={v} style={{ marginBottom: 10 }}>
                                    <CommonIriDisplay
                                        key={iwr.id}
                                        id={iwr.id}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={defaultLang}
                                        defaultLang={defaultLang} />
                                    <ArrowRightIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                    <Chip color="primary"
                                        data-test="target-property"
                                        className={classes.inactiveChip}
                                        label={v} />
                                </div>
                            );
                        }
                    }
                />
            }
            {ontologyProjectAssociation && skosXL &&
                <QualityCheck
                    dataTest="associated-property-labels"
                    title="Associated Property Range Integrity On A Label"
                    issues={issues.associatedpropertyrangelabels}
                    isLoading={isLoading}
                    graph={graph}
                    isReadOnly={isReadOnly}
                    sortLabel="prefLabel"
                    defaultLang={defaultLang}
                    loadProps={["id", "inconsistentWithRange"]}
                    disabled={disabledFeatures && disabledFeatures.includes("quality-associatedpropertyrangelabels")}
                    onDisable={(disable) => onDisable("associatedpropertyrangelabels", disable)}
                    onRefresh={() => onRefresh("associatedpropertyrangelabels")}
                    errors={issues["associatedpropertyrangelabels-error"]}
                    fromCache={issues["associatedpropertyrangelabels-time"]}
                    meaning={<div>
                        <Typography variant="body2" color="textPrimary">If a property has a range it is expected that the target resource will have the range as a class.</Typography>
                        <Typography variant="body2" color="textPrimary">The following labels use an associated property whose target does not have classes that are stated on the range of the associated property.</Typography>
                        <Typography variant="body2" color="textPrimary">You can probably resolve these issues by adding the class to the target, or alternatively, check the range on the property.</Typography>
                    </div>}
                    issueDisplay={
                        (iwr) => {
                            return iwr.inconsistentWithRange && iwr.inconsistentWithRange.map(v =>
                                <div key={v} style={{ marginBottom: 10 }}>
                                    <CommonIriDisplay
                                        id={iwr.id}
                                        key={iwr.id}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={defaultLang}
                                        defaultLang={defaultLang} />
                                    <ArrowRightIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                    <Chip color="primary"
                                        data-test="target-property"
                                        className={classes.inactiveChip}
                                        label={v} />
                                </div>
                            );
                        }
                    }
                />
            }
            {ontologyProjectAssociation &&
                <QualityCheck
                    dataTest="asymmetric"
                    title="Asymmetric Relationship Integrity On A Concept"
                    issues={issues.asymmetric}
                    isLoading={isLoading}
                    graph={graph}
                    isReadOnly={isReadOnly}
                    sortLabel="prefLabel"
                    defaultLang={defaultLang}
                    loadProps={["id", "asymmetricViolation"]}
                    disabled={disabledFeatures && disabledFeatures.includes("quality-asymmetric")}
                    onDisable={(disable) => onDisable("asymmetric", disable)}
                    onRefresh={() => onRefresh("asymmetric")}
                    errors={issues["asymmetric-error"]}
                    fromCache={issues["asymmetric-time"]}
                    meaning={<div>
                        <Typography variant="body2" color="textPrimary">An asymmetric property implies that the relationship should not hold in both directions.</Typography>
                        <Typography variant="body2" color="textPrimary">The following concepts are connected in both directions by an asymmetric property.</Typography>
                        <Typography variant="body2" color="textPrimary">You can probably resolve these issues by removing the relationship in one direction.</Typography>
                    </div>}
                    issueDisplay={
                        (asy) => {
                            return asy.asymmetricViolation &&
                                <div style={{ marginBottom: 10 }}>
                                    <CommonIriDisplay
                                        id={asy.id}
                                        key={asy.id}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={defaultLang}
                                        defaultLang={defaultLang} />
                                    <ArrowLeftIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                    {asy.asymmetricViolation.sort().map(v => {
                                        return <CommonIriDisplay
                                            id={v}
                                            key={v}
                                            compact={true}
                                            projectIRI={getProjectFromGraph(graph)}
                                            handleNotAvailable={true}
                                            loadComponents={true}
                                            graph={graph}
                                            wrap={false}
                                            onLink={onToggleView}
                                            viewLang={defaultLang}
                                            defaultLang={defaultLang} />;
                                    })}
                                </div>;
                        }
                    }
                />
            }
            {ontologyProjectAssociation && skosXL &&
                <QualityCheck
                    dataTest="asymmetric-labels"
                    title="Asymmetric Relationship Integrity On A Label"
                    issues={issues.asymmetriclabels}
                    isLoading={isLoading}
                    graph={graph}
                    isReadOnly={isReadOnly}
                    sortLabel="prefLabel"
                    defaultLang={defaultLang}
                    loadProps={["id", "asymmetricViolation"]}
                    disabled={disabledFeatures && disabledFeatures.includes("quality-asymmetriclabels")}
                    onDisable={(disable) => onDisable("asymmetriclabels", disable)}
                    onRefresh={() => onRefresh("asymmetriclabels")}
                    errors={issues["asymmetriclabels-error"]}
                    fromCache={issues["asymmetriclabels-time"]}
                    meaning={<div>
                        <Typography variant="body2" color="textPrimary">An asymmetric property implies that the relationship should not hold in both directions.</Typography>
                        <Typography variant="body2" color="textPrimary">The following label are connected in both directions by an asymmetric property.</Typography>
                        <Typography variant="body2" color="textPrimary">You can probably resolve these issues by removing the relationship in one direction.</Typography>
                    </div>}
                    issueDisplay={
                        (asy) => {
                            return asy.asymmetricViolation &&
                                <div style={{ marginBottom: 10 }}>
                                    <CommonIriDisplay
                                        id={asy.id}
                                        key={asy.id}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={defaultLang}
                                        defaultLang={defaultLang} />
                                    <ArrowLeftIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                    {asy.asymmetricViolation.sort().map(v => {
                                        return <CommonIriDisplay
                                            id={v}
                                            key={v}
                                            compact={true}
                                            projectIRI={getProjectFromGraph(graph)}
                                            handleNotAvailable={true}
                                            loadComponents={true}
                                            graph={graph}
                                            wrap={false}
                                            onLink={onToggleView}
                                            viewLang={defaultLang}
                                            defaultLang={defaultLang} />;
                                    })}
                                </div>;
                        }
                    }
                />
            }
        </div>;
    };

    render() {

        const { filteredViews } = this.state;
        const { id, graph, data } = this.props;

        return (
            <div>
                <QualityCheckLoader
                    id={id}
                    graph={graph}
                    displayResults={this.displayResults}
                    disabledFeatures={data.disabledFeatures}
                    views={filteredViews} />
            </div>
        );
    }

}

SchemeQuality.propTypes = {
    onToggleView: PropTypes.func.isRequired,
    id: PropTypes.string.isRequired,
    onDisable: PropTypes.func.isRequired,
    data: PropTypes.object.isRequired,
    link: PropTypes.string.isRequired,
    skosXL: PropTypes.bool.isRequired,
    history: PropTypes.object,
    defaultLang: PropTypes.string.isRequired,
    ontologyProjectAssociation: PropTypes.array,
    graph: PropTypes.string.isRequired,
    entitlement: PropTypes.string.isRequired,
    classes: PropTypes.object
};

export default withStyles(styles)(withRouter(SchemeQuality));